import { api } from "@repo/convex/api";
import { useObservedAction, useObservedMutation } from "@/hooks/use-observed-convex";

/**
 * Hook for persisting mention references for an entity rich-text field.
 * @returns Mutation function for mentions.saveForEntity
 */
export function useSaveMentionsForEntity() {
  return useObservedMutation(api.mentions.saveForEntity, {
    feature: "mentions",
    operation: "save-for-entity",
    includeRequestId: true,
  });
}

/**
 * Hook for persisting data catalog embed references for an entity rich-text field.
 * @returns Mutation function for catalogEmbeds.saveForEntity
 */
export function useSaveCatalogEmbedsForEntity() {
  return useObservedMutation(api.catalogEmbeds.saveForEntity, {
    feature: "catalog-embeds",
    operation: "save-for-entity",
    includeRequestId: true,
  });
}

/**
 * Hook for creating a version record.
 * @returns Mutation function for versions.create
 */
export function useCreateVersion() {
  return useObservedMutation(api.versions.create, {
    feature: "versions",
    operation: "create",
    includeRequestId: true,
  });
}

/**
 * Hook for atomically saving a project entity with a version record.
 * @returns Mutation function for projectEntities.saveWithVersion
 */
export function useSaveProjectEntityWithVersion() {
  return useObservedMutation(api.projectEntities.saveWithVersion, {
    feature: "project-entities",
    operation: "save-with-version",
    includeRequestId: true,
  });
}

/**
 * Hook for creating a project snapshot.
 * @returns Mutation function for versions.createProjectSnapshot
 */
export function useCreateProjectSnapshot() {
  return useObservedMutation(api.versions.createProjectSnapshot, {
    feature: "versions",
    operation: "create-project-snapshot",
    includeRequestId: true,
  });
}

/**
 * Hook for restoring a project snapshot.
 * @returns Mutation function for versions.restoreProjectSnapshot
 */
export function useRestoreProjectSnapshot() {
  return useObservedMutation(api.versions.restoreProjectSnapshot, {
    feature: "versions",
    operation: "restore-project-snapshot",
    includeRequestId: true,
  });
}

/**
 * Hook for naming a brand-new element the moment its author commits the header (LAT-983).
 *
 * Best-effort by contract: the mutation writes only `entities.name`, mints no version and does not
 * stamp `updatedAt`, and it refuses once the element has a saved version. A failure costs the user
 * nothing that Save will not put right, so callers fire and forget — the observed wrapper reports
 * it.
 * @returns Mutation function for projectEntities.nameNewEntity
 */
export function useNameNewEntity() {
  return useObservedMutation(api.projectEntities.nameNewEntity, {
    feature: "project-entities",
    operation: "name-new-entity",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
}

/**
 * Hook for upserting a draft version (create or update).
 * @returns Mutation function for versions.upsertDraft
 */
export function useUpsertDraft() {
  return useObservedMutation(api.versions.upsertDraft, {
    feature: "versions",
    operation: "upsert-draft",
    includeRequestId: true,
  });
}

/**
 * Hook for deleting a user's draft version.
 * @returns Mutation function for versions.deleteDraft
 */
export function useDeleteDraft() {
  return useObservedMutation(api.versions.deleteDraft, {
    feature: "versions",
    operation: "delete-draft",
    includeRequestId: true,
  });
}

/**
 * Hook for recording an entity visit for recent activity.
 * @returns Mutation function for userEntityActivity.recordVisit
 */
export function useRecordEntityVisit() {
  return useObservedMutation(api.userEntityActivity.recordVisit, {
    feature: "user-entity-activity",
    operation: "record-visit",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
}

/**
 * Hook for recording a touch/edit interaction for recent activity.
 * @returns Mutation function for userEntityActivity.recordTouch
 */
export function useRecordEntityTouch() {
  return useObservedMutation(api.userEntityActivity.recordTouch, {
    feature: "user-entity-activity",
    operation: "record-touch",
    includeRequestId: true,
    failureOutcome: "best_effort_failure",
  });
}

/**
 * Hook for creating a folder.
 * @returns Mutation function for folders.create
 */
export function useCreateFolder() {
  return useObservedMutation(api.folders.create, {
    feature: "folders",
    operation: "create",
    includeRequestId: true,
  });
}

/**
 * Hook for creating a project from the active org framework assignment.
 * @returns Mutation function for projects.createFromActiveFramework
 */
export function useCreateProjectFromActiveFramework() {
  return useObservedMutation(api.projects.createFromActiveFramework, {
    feature: "projects",
    operation: "create-from-active-framework",
  });
}

/**
 * Hook for updating a project (rename, description, etc.).
 * @returns Mutation function for projects.update
 */
export function useUpdateProject() {
  return useObservedMutation(api.projects.update, {
    feature: "projects",
    operation: "update",
    includeRequestId: true,
  });
}

/**
 * Hook for soft-deleting a project.
 * @returns Mutation function for projects.remove
 */
export function useRemoveProject() {
  return useObservedMutation(api.projects.remove, {
    feature: "projects",
    operation: "remove",
    includeRequestId: true,
  });
}

/**
 * Hook for moving a project to a folder (or to root/unfiled).
 * @returns Mutation function for folders.moveProject
 */
export function useMoveProject() {
  return useObservedMutation(api.folders.moveProject, {
    feature: "folders",
    operation: "move-project",
    includeRequestId: true,
  });
}

/**
 * Hook for renaming a folder.
 * @returns Mutation function for folders.rename
 */
export function useRenameFolder() {
  return useObservedMutation(api.folders.rename, {
    feature: "folders",
    operation: "rename",
    includeRequestId: true,
  });
}

/**
 * Hook for soft-deleting a folder.
 * @returns Mutation function for folders.remove
 */
export function useRemoveFolder() {
  return useObservedMutation(api.folders.remove, {
    feature: "folders",
    operation: "remove",
    includeRequestId: true,
  });
}

/**
 * Hook for moving a folder to a new parent (or to root).
 * @returns Mutation function for folders.moveFolder
 */
export function useMoveFolder() {
  return useObservedMutation(api.folders.moveFolder, {
    feature: "folders",
    operation: "move-folder",
    includeRequestId: true,
  });
}

/**
 * Hook for creating a project tree folder.
 * @returns Mutation function for projectTreeFolders.create
 */
export function useCreateProjectTreeFolder() {
  return useObservedMutation(api.projectTreeFolders.create, {
    feature: "project-tree-folders",
    operation: "create",
    includeRequestId: true,
  });
}

/**
 * Hook for renaming a project tree folder.
 * @returns Mutation function for projectTreeFolders.rename
 */
export function useRenameProjectTreeFolder() {
  return useObservedMutation(api.projectTreeFolders.rename, {
    feature: "project-tree-folders",
    operation: "rename",
    includeRequestId: true,
  });
}

/**
 * Hook for removing a project tree folder.
 * @returns Mutation function for projectTreeFolders.remove
 */
export function useRemoveProjectTreeFolder() {
  return useObservedMutation(api.projectTreeFolders.remove, {
    feature: "project-tree-folders",
    operation: "remove",
    includeRequestId: true,
  });
}

// ============================================================================
// Entity Relations
// ============================================================================

/**
 * Hook for creating an entity relation edge.
 * @returns Mutation function for entityRelations.create
 */
export function useCreateRelation() {
  return useObservedMutation(api.entityRelations.create, {
    feature: "entity-relations",
    operation: "create",
    includeRequestId: true,
  });
}

/**
 * Hook for removing an entity relation (soft delete).
 * @returns Mutation function for entityRelations.remove
 */
export function useRemoveRelation() {
  return useObservedMutation(api.entityRelations.remove, {
    feature: "entity-relations",
    operation: "remove",
    includeRequestId: true,
  });
}

// ============================================================================
// Artifacts
// ============================================================================

export function useGenerateArtifactUploadUrl() {
  return useObservedMutation(api.artifacts.generateUploadUrl, {
    feature: "artifacts",
    operation: "generate-upload-url",
    includeRequestId: true,
  });
}

export function useCreateFileArtifact() {
  return useObservedMutation(api.artifacts.createFile, {
    feature: "artifacts",
    operation: "create-file",
    includeRequestId: true,
  });
}

export function useGenerateMediaUploadUrl() {
  return useObservedMutation(api.artifactMedia.generateMediaUploadUrl, {
    feature: "artifacts",
    operation: "generate-media-upload-url",
    includeRequestId: true,
  });
}

export function useCreateMediaFileArtifact() {
  return useObservedAction(api.artifactMedia.createMediaFile, {
    feature: "artifacts",
    operation: "create-media-file",
    includeRequestId: true,
    // Safe to re-run: `createOrReuseMediaArtifact` returns the artifact already registered
    // for this `r2Key`.
    retryOnConnectionLoss: true,
  });
}

export function useRetryArtifactProcessingJob() {
  return useObservedMutation(api.artifactProcessing.retryJob, {
    feature: "artifact-processing",
    operation: "retry-job",
    includeRequestId: true,
  });
}
